import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useMutation, useQueries } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { validateAllocationTemplatePercents } from 'common/src/allocations/validateAllocationTemplateDetails';
import { SingleOperators } from 'common/src/query-params/operators';
import { IGenerateSubMeterDto } from 'common/src/types/account.dtos';
import {
  ICreateAllocationTemplate,
  IUpdateAllocationTemplate,
} from 'common/src/types/allocationTemplateDto';
import { AlertSubtype } from 'common/src/types/enums/AlertSubtype.enum';
import { AllocationCostMethod } from 'common/src/types/enums/AllocationCostMethod.enum';
import { AllocationType } from 'common/src/types/enums/AllocationType.enum';
import { AllocationUsageMethod } from 'common/src/types/enums/AllocationUsageMethod.enum';
import {
  IAccount,
  IAllocationTemplate,
  IAllocationTemplateDetail,
  IFacility,
  IMeter,
  IPage,
} from 'common/src/types/interfaces';

import {
  generateSubAccount,
  getAccount,
  getAccounts,
} from 'portal/src/api/AccountsService';
import {
  addAllocationTemplate,
  deleteAllocationTemplate,
  updateAllocationTemplate,
} from 'portal/src/api/AllocationService';
import { getFacilities } from 'portal/src/api/FacilitiesService';
import useAuth from 'portal/src/auth/useAuth';
import ConfirmDialog from 'portal/src/components/common/ConfirmDialog';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import FormController from 'portal/src/components/common/forms/FormController';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import AddSubAccountDetailsDialog from './AddSubAccountDetailsDialog';
import GenerateSubAccountDialog from './GenerateSubAccountDialog';
import SubAccountDetailRow from './SubAccountDetailRow';

import LineItemDetails from './details/LineItemDetails';
import MonthlyPercentageDetails from './details/MonthlyPercentageDetails';
import TotalPercentDetails from './details/TotalPercentDetails';

export type FormValues = {
  type: AllocationType;
  needsReview: boolean;
  bypassAutomaticCreation: boolean;
  details: SubAccountDetailRow[];
};

function buildSubAccountDetailRows(
  template: IAllocationTemplate,
): SubAccountDetailRow[] {
  const { details = [], type } = template;

  if (type === AllocationType.MonthlyPercentage) {
    const numbers = new Map<string, string>();
    const monthData = new Map<string, Map<number, SubAccountDetailRow>>();
    for (const d of details) {
      numbers.set(d.subAccountId, d.subAccount?.number ?? d.subAccountId);
      if (!monthData.has(d.subAccountId))
        monthData.set(d.subAccountId, new Map());
      monthData.get(d.subAccountId).set(d.budgetMonth ?? 0, {
        subAccountId: d.subAccountId,
        number: d.subAccount?.number ?? d.subAccountId,
        databaseId: d.id,
        budgetMonth: d.budgetMonth ?? 0,
        totalPercent: '',
        costPercent: d.costPercent ?? '',
        usagePercent: d.usagePercent ?? '',
      });
    }
    return Array.from(numbers.entries()).flatMap(([subAccountId, number]) =>
      Array.from({ length: 12 }, (_, i) => {
        const existing = monthData.get(subAccountId)?.get(i);
        return (
          existing ?? {
            subAccountId,
            number,
            budgetMonth: i,
            totalPercent: '',
            costPercent: '',
            usagePercent: '',
          }
        );
      }),
    );
  }

  return details.map((d) => ({
    subAccountId: d.subAccountId,
    number: d.subAccount?.number ?? d.subAccountId,
    databaseId: d.id,
    totalPercent:
      type === AllocationType.TotalPercent ? (d.costPercent ?? '') : '',
    costPercent: d.costPercent ?? '',
    usagePercent: d.usagePercent ?? '',
  }));
}

function getDetailsPayload(rows: SubAccountDetailRow[], type: AllocationType) {
  return rows.map((row) => {
    const percent = row.totalPercent === '' ? null : Number(row.totalPercent);
    const costPercent =
      type === AllocationType.TotalPercent
        ? percent
        : row.costPercent === ''
          ? null
          : Number(row.costPercent);
    const usagePercent =
      type === AllocationType.TotalPercent
        ? percent
        : row.usagePercent === ''
          ? null
          : Number(row.usagePercent);
    return {
      id: row.databaseId ?? null,
      budgetMonth: row.budgetMonth,
      costMethod: AllocationCostMethod.Percentage,
      costPercent,
      subAccountId: row.subAccountId,
      usageMethod: AllocationUsageMethod.Percentage,
      usagePercent,
    };
  });
}

const AllocationTemplateForm = ({
  masterAccountId,
  masterMeters,
  template: templateParam,
}: {
  masterAccountId: string;
  masterMeters: IMeter[];
  template: IAllocationTemplate;
}) => {
  const snackbarHandler = useSnackbarHandler();
  const navigate = useNavigate();
  const { getUser } = useAuth();
  const { organizationId } = getUser();

  const template = templateParam ?? ({} as IAllocationTemplate);

  const [
    {
      data: subAccountsPage,
      isFetching: isSubAccountsFetching,
      refetch: refetchSubAccounts,
    },
    { data: facilitiesPage, isFetching: isFacilitiesFetching },
  ] = useQueries({
    queries: [
      {
        queryKey: ['subAccounts', masterAccountId],
        queryFn: () =>
          getAccounts({
            filters: [
              {
                field: 'masterAccountId',
                operator: SingleOperators.EQUALS,
                value: masterAccountId,
              },
              {
                field: 'isActive',
                operator: SingleOperators.EQUALS,
                value: true,
              },
            ],
          }),
        initialData: { data: [] } as IPage<IAccount>,
        enabled: !!masterAccountId,
      },
      {
        queryKey: ['facilities'],
        queryFn: () => getFacilities(),
        initialData: { data: [] } as IPage<IFacility>,
      },
    ],
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      type: template.type ?? AllocationType.TotalPercent,
      needsReview: template.needsReview ?? false,
      bypassAutomaticCreation: template.bypassAutomaticCreation ?? false,
      details: buildSubAccountDetailRows(template),
    },
  });

  const [type, detailsWatch] = useWatch({ name: ['type', 'details'], control });
  // Memoize the details to prevent reassigning empty array values and recalculating the available sub accounts array
  const details = useMemo(() => detailsWatch ?? [], [detailsWatch]);

  const [showAddSubAccountDialog, setShowAddSubAccountDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    string[] | undefined
  >();

  const availableSubAccounts = useMemo(() => {
    const usedSubAccounts = new Set(details.map((f) => f.subAccountId));
    return (subAccountsPage?.data ?? []).filter(
      (a) => !usedSubAccounts.has(a.id),
    );
  }, [subAccountsPage, details]);

  const { mutate: saveOrUpdate, isPending: isSaving } = useMutation({
    mutationKey: ['saveAllocationTemplate', template.id],
    mutationFn: (payload: IUpdateAllocationTemplate) => {
      if (template.id) {
        return updateAllocationTemplate(template.id, payload);
      }
      return addAllocationTemplate({
        ...payload,
        masterAccountId,
        organizationId,
      } as ICreateAllocationTemplate);
    },
    onSuccess: () => {
      snackbarHandler.success(
        template.id
          ? MESSAGES.ALLOCATIONS.TEMPLATE.UPDATED
          : MESSAGES.ALLOCATIONS.TEMPLATE.CREATED,
      );
      handleNavigate();
    },
    onError: () => snackbarHandler.error(MESSAGES.GENERIC.ERROR),
  });

  const { mutate: tryGenerateSubAccount, isPending: isGenerating } =
    useMutation({
      mutationKey: ['generateSubAccount', masterAccountId],
      mutationFn: (subMeters: IGenerateSubMeterDto[]) =>
        generateSubAccount(masterAccountId, { subMeters }),
      onSuccess: async (newSubAccountId: string) => {
        setShowGenerateDialog(false);
        const newSubAccount = await getAccount(newSubAccountId);
        addSubAccountDetail(newSubAccount);
        snackbarHandler.success('Sub account generated successfully.');
        await refetchSubAccounts();
      },
      onError: () => snackbarHandler.error(MESSAGES.GENERIC.ERROR),
    });

  const handleNavigate = () => {
    navigate('..');
  };

  const { mutate: deleteTemplate, isPending: isDeleting } = useMutation({
    mutationKey: ['deleteAllocationTemplate', template.id],
    mutationFn: () => deleteAllocationTemplate(template.id),
    onSuccess: () => {
      snackbarHandler.success(MESSAGES.ALLOCATIONS.TEMPLATE.DELETED);
      handleNavigate();
    },
    onError: () => snackbarHandler.error(MESSAGES.GENERIC.ERROR),
  });

  const handleTypeChange = (newType: AllocationType) => {
    const distinctSubs = Array.from(
      new Map(
        details.map((d) => [
          d.subAccountId,
          { subAccountId: d.subAccountId, number: d.number },
        ]),
      ).values(),
    );

    if (newType === AllocationType.MonthlyPercentage) {
      setValue(
        'details',
        distinctSubs.flatMap(({ subAccountId, number }) =>
          Array.from({ length: 12 }, (_, i) => ({
            subAccountId,
            number,
            budgetMonth: i,
            totalPercent: '' as const,
            costPercent: '' as const,
            usagePercent: '' as const,
          })),
        ),
        { shouldDirty: true },
      );
    } else {
      setValue(
        'details',
        distinctSubs.map(({ subAccountId, number }) => ({
          subAccountId,
          number,
          totalPercent: '' as const,
          costPercent: '' as const,
          usagePercent: '' as const,
        })),
        { shouldDirty: true },
      );
    }

    // clear existing validation errors
    setValidationErrors(undefined);
  };

  const addSubAccountDetail = (subAccountToAdd: IAccount) => {
    const newDetailRow = {
      subAccountId: subAccountToAdd.id,
      number: subAccountToAdd.number,
      totalPercent: '' as const,
      costPercent: '' as const,
      usagePercent: '' as const,
    };

    if (type === AllocationType.MonthlyPercentage) {
      setValue(
        'details',
        [
          ...details,
          ...Array.from({ length: 12 }, (_, i) => ({
            ...newDetailRow,
            budgetMonth: i,
          })),
        ],
        { shouldDirty: true },
      );
    } else {
      setValue('details', [...details, newDetailRow], { shouldDirty: true });
    }
  };

  const handleRemoveRow = (subAccountId: string) => {
    setValue(
      'details',
      details.filter((f) => f.subAccountId !== subAccountId),
      { shouldDirty: true },
    );
  };

  const handleSave = handleSubmit((data) => {
    const detailsPayload = getDetailsPayload(data.details, data.type);

    const validationResult = validateAllocationTemplatePercents(
      type,
      detailsPayload as IAllocationTemplateDetail[],
    );

    if (!validationResult.success) {
      setValidationErrors(validationResult.errors);
      return;
    }

    saveOrUpdate({
      type: data.type,
      needsReview: data.needsReview,
      bypassAutomaticCreation: data.bypassAutomaticCreation,
      details: detailsPayload,
    });
  });

  const isMissingMasterMeters = masterMeters.length === 0;

  const generateSubAccountDisabled =
    isGenerating ||
    isSubAccountsFetching ||
    isFacilitiesFetching ||
    isMissingMasterMeters;

  const isPending = isSaving || isGenerating || isDeleting;

  return (
    <FormContainer
      control={control}
      extraButtons={
        template.id
          ? [
              {
                onClick: () => setShowDeleteDialog(true),
                label: 'Delete',
              },
            ]
          : undefined
      }
      handleBackAction={handleNavigate}
      handleSubmit={handleSave}
      header="Allocation Settings"
      isDirty={isDirty}
      isNew={!template.id}
      isPending={isPending}
      mainFieldsConfig={[
        {
          infoTooltip: `When set, an "${AlertSubtype.AllocationNeedsAttention}" alert will be created when allocated bills are generated using this template.`,
          label: 'Needs Review',
          name: 'needsReview',
          required: false,
          type: 'switch',
        },
        {
          infoTooltip:
            'When set, allocated bills will not be automatically created when a new bill is added for this account.',
          label: 'Bypass Automatic Creation',
          name: 'bypassAutomaticCreation',
          required: false,
          type: 'switch',
        },
      ]}
      afterMainFields={
        <>
          <Divider>Details</Divider>
          <Stack spacing={2}>
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
              <Box sx={{ width: 200 }}>
                <FormController
                  control={control}
                  fieldConf={{
                    name: 'type',
                    label: 'Detail Type',
                    onChange: handleTypeChange,
                    placeholder: 'Allocation Type',
                    type: 'selectInput',
                    required: true,
                    size: 'small',
                    values: Object.values(AllocationType).map((t) => ({
                      label: t,
                      value: t,
                    })),
                  }}
                />
              </Box>
              <Button
                color="secondary"
                disableElevation
                onClick={() => setShowAddSubAccountDialog(true)}
                size="small"
                startIcon={<AddIcon />}
                variant="outlined"
              >
                Add Sub Account Details
              </Button>
            </Stack>

            <Divider />

            {details.length === 0 && (
              <Typography color="text.secondary" variant="body2">
                No sub accounts configured.
              </Typography>
            )}

            {type === AllocationType.TotalPercent && details.length > 0 && (
              <TotalPercentDetails
                control={control}
                validationErrors={validationErrors}
                handleRemoveRow={handleRemoveRow}
                rows={details}
              />
            )}

            {type === AllocationType.LineItemDetails && details.length > 0 && (
              <LineItemDetails
                control={control}
                validationErrors={validationErrors}
                handleRemoveRow={handleRemoveRow}
                rows={details}
              />
            )}

            {type === AllocationType.MonthlyPercentage && (
              <MonthlyPercentageDetails
                control={control}
                validationErrors={validationErrors}
                handleRemoveRow={handleRemoveRow}
                rows={details}
              />
            )}
          </Stack>
          <AddSubAccountDetailsDialog
            availableSubAccounts={availableSubAccounts}
            generateDisabled={generateSubAccountDisabled}
            isMissingMasterMeters={isMissingMasterMeters}
            onAddExisting={(account) => {
              setShowAddSubAccountDialog(false);
              addSubAccountDetail(account);
            }}
            onCancel={() => setShowAddSubAccountDialog(false)}
            onGenerateNew={() => {
              setShowAddSubAccountDialog(false);
              setShowGenerateDialog(true);
            }}
            open={showAddSubAccountDialog}
          />
          <GenerateSubAccountDialog
            facilities={facilitiesPage?.data ?? []}
            disabled={generateSubAccountDisabled}
            masterMeters={masterMeters}
            onCancel={() => setShowGenerateDialog(false)}
            onConfirm={(subMeters) => tryGenerateSubAccount(subMeters)}
            open={showGenerateDialog}
          />
          <ConfirmDialog
            open={showDeleteDialog}
            onClose={() => setShowDeleteDialog(false)}
            message="Are you sure you want to delete these allocation settings?"
            primaryButtonLabel="Delete"
            primaryButtonDisabled={isDeleting}
            onPrimaryButtonClicked={() => deleteTemplate()}
            secondaryButtonLabel="Cancel"
            onSecondaryButtonClicked={() => setShowDeleteDialog(false)}
            renderElement={<DeleteIcon sx={{ color: 'error.main' }} />}
          />
        </>
      }
    />
  );
};

export default AllocationTemplateForm;
